import Web3 from 'web3'
import * as Web3Utils from 'web3-utils'
import { Contract } from 'web3-eth-contract'
// import { SellType, BuyType, DomainType } from './utils'
import './types'
import * as BN from 'bn.js'

import nftFactoryABI_ver1 from './abi/ver1/nftABI'
import nftMarketABI_ver1 from './abi/ver1/nftMarketABI'
import ERC721ABI_ver1 from './abi/ver1/erc721ABI'
import ERC20ABI_ver1 from './abi/ver1/erc20ABI'

import {
    ContractConfig,
    AtomicMatchParams,
    BuyerSignatureData,
    SellerSignatureData,
    ContractVersion,
} from './types'

/*
 * Get all the addresses
 * @param isProd is production?
 * @param ver is contract's version
 * @returns {ContractConfig}
 */
export function createConfig(
    isProd: boolean,
    ver: ContractVersion
): ContractConfig {
    const versions: ContractConfig[] = [
        {
            ver: 1,
            main: {
                addresses: isProd
                    ? {
                          factory: '0x8eebae4af0704dcba2bc0f401a9915d5f119ee5a',
                          marketplace:
                              '0x5aae8ed4f435681d448f1e0ecebddc9e9cb5ba9d',
                          token: '0xdb0820dc19726062a95c52c3ac037084d51f8b4e',
                      }
                    : {
                          factory: '0x8A24A9FA1A1c1273be7136451a18471e05B16b06',
                          marketplace:
                              '0x3a1d638e0929e45af9377c071c7f0ded26a45e16',
                          token: '0xa6a778cfbd62646d5940b6cd74db11f85f32feca',
                      },
                abi: {
                    nftFactoryABI: nftFactoryABI_ver1,
                    nftMarketABI: nftMarketABI_ver1,
                    ERC20ABI: ERC20ABI_ver1,
                    ERC721ABI: ERC721ABI_ver1,
                },
            },
        },
    ]

    return versions.find((v) => v.ver === ver)!
}

export class Contracts {
    public nftFactory!: Contract
    public nftMarketplace!: Contract
    public CALToken!: Contract

    constructor(private config: ContractConfig, public web3: Web3) {
        this.updateConfig(config)
    }

    public updateConfig = (config: ContractConfig) => {
        this.config = config
        this.nftFactory = new this.web3.eth.Contract(
            this.abi.nftFactoryABI,
            this.addresses.factory
        )
        this.nftMarketplace = new this.web3.eth.Contract(
            this.abi.nftMarketABI,
            this.addresses.marketplace
        )
        this.CALToken = new this.web3.eth.Contract(
            this.abi.ERC20ABI,
            this.addresses.token
        )
        return this
    }

    public updateWeb3 = (web3: Web3) => {
        this.web3 = web3
        return this
    }

    get abi() {
        return this.config.main.abi
    }

    get addresses() {
        return this.config.main.addresses
    }

    get contractVersion() {
        return this.config.ver
    }
}
/**
 *
 */
export class ContractsWrapper {
    constructor(private chainId: number, private contracts: Contracts) {}

    get contractVersion() {
        return this.contracts.contractVersion
    }

    getNFTContract = (address: string): Contract => {
        return new this.contracts.web3.eth.Contract(
            this.contracts.abi.ERC721ABI,
            address
        )
    }

    getTokenOwner = async (tokenId: BN): Promise<string> => {
        return await this.contracts.nftFactory.methods.ownerOf(tokenId).call()
    }

    /**
     * Mints new NFTs
     * @param to Recipient of NFT
     * @param amountOfCopies Amount of tokens to mint
     * @returns NFT ids
     */
    createNewNFT = async (
        to: string,
        amountOfCopies: number = 1
    ): Promise<number[]> => {
        const receipt = await this.contracts.nftFactory.methods
            .mint(amountOfCopies, to)
            .send({
                from: this.contracts.web3.eth.defaultAccount,
                gas: 2000000,
            })
        return receipt.events.TokensMined.returnValues[0]
    }

    /** Allows to donate to creator
     *  @param to Recipient
     *  @param amount Amount of tokens to donate
     */
    donateToCreator = async (to: string, amount: BN) => {
        // approve funds
        const response = await this.contracts.CALToken.methods
            .approve(this.contracts.nftMarketplace.options.address, amount)
            .send({ from: this.contracts.web3.eth.defaultAccount, gas: 200000 })

        // waiting until funds will be approved
        let receipt = await this.contracts.web3.eth.getTransactionReceipt(
            response.transactionHash
        )
        while (receipt == null) {
            await new Promise((resolve) => setTimeout(resolve, 1000))
            receipt = await this.contracts.web3.eth.getTransactionReceipt(
                response.transactionHash
            )
        }

        // donate funds
        await this.contracts.nftMarketplace.methods
            .donateToCreator(to, amount)
            .send({
                from: this.contracts.web3.eth.defaultAccount,
                gas: 4368166,
            })
    }

    /**
     * Returns all donations by user
     * @returns Donations made by selected user
     */
    getDonationsByUser = async (from: string) => {
        const account = Web3Utils.toChecksumAddress(from)
        const donations = await this.contracts.nftMarketplace.getPastEvents(
            'Donation',
            {
                filter: { from: account },
                fromBlock: 0,
                toBlock: 'latest',
            }
        )

        return donations.filter((event) => {
            const { _sender } = event.returnValues
            return _sender === from
        })
    }

    /**
     * Returns all donations by user
     * @returns Donations made by selected user
     */
    getDonationsToCreator = async (creator: string) => {
        const donations = await this.contracts.nftMarketplace.getPastEvents(
            'Donation',
            {
                fromBlock: 0,
                toBlock: 'latest',
            }
        )

        return donations.filter((event) => {
            const { _creator } = event.returnValues
            return _creator === creator
        })
    }

    /**
     * Returns domain data to create typed signature for eth_signTypedData_v4
     * @returns
     */
    private getDomainData = () => {
        return {
            name: 'CAL NFT Marketplace',
            version: '1',
            chainId: this.chainId,
            verifyingContract: this.contracts.nftMarketplace.options.address,
        }
    }

    getOwnedItems = async (
        contractAddress: string,
        ownerAddress: string
    ): Promise<BN[]> => {
        const account = Web3Utils.toChecksumAddress(ownerAddress)
        const token = this.getNFTContract(contractAddress)
        const sent = await token.getPastEvents('Transfer', {
            filter: { from: account },
            fromBlock: 0,
            toBlock: 'latest',
        })

        const received = await token.getPastEvents('Transfer', {
            filter: { to: account },
            fromBlock: 0,
            toBlock: 'latest',
        })

        const logs = sent
            .concat(received)
            .sort(
                (a, b) =>
                    a.blockNumber - b.blockNumber ||
                    a.transactionIndex - b.transactionIndex
            )

        const owned = new Set<BN>()

        for (const log of logs) {
            const { from, to, tokenId } = log.returnValues

            if (to === account) {
                owned.add(tokenId.toString())
            } else if (from === account) {
                owned.delete(tokenId.toString())
            }
        }

        return Array.from(owned)
    }

    getTokenURI = async (
        contractAddress: string,
        tokenId: BN
    ): Promise<string> => {
        const token = this.getNFTContract(contractAddress)
        return await token.methods.tokenURI(tokenId).call()
    }

    /**
     * Creates seller signature. Should be called from frontend before NFT approval
     * @param sellerSignatureData Seller data to sign
     * @returns Seller signature
     */
    createSellerSignature = async (params: SellerSignatureData) => {
        const { orderMaker, isFixedPrice, price, itemId, itemContractAddress } =
            params

        const sellerData = this.contracts.web3.utils.soliditySha3(
            orderMaker,
            isFixedPrice,
            price,
            itemId,
            itemContractAddress
        )

        const sellerSignature = await window.ethereum?.request({
            method: 'personal_sign',
            params: [window.ethereum.selectedAddress, sellerData],
            from: window.ethereum.selectedAddress,
        })

        return sellerSignature
    }

    /**
     * Creates buyer signature. Should be called from frontend before CAL tokens approval
     * @param buyerSignatureData
     * @returns
     */
    createBuyerSignature = async (params: BuyerSignatureData) => {
        const {
            orderTaker,
            isFixedPrice,
            price,
            extra,
            itemId,
            itemContractAddress,
        } = params
        const buyerData = this.contracts.web3.utils.soliditySha3(
            orderTaker,
            isFixedPrice,
            price,
            extra,
            itemId,
            itemContractAddress
        )

        let buyerSignature = await window.ethereum?.request({
            method: 'personal_sign',
            params: [window.ethereum.selectedAddress, buyerData],
            from: window.ethereum.selectedAddress,
        })

        return buyerSignature
    }

    atomicMatch = async (params: AtomicMatchParams) => {
        const order = {
            creator: params.creator,
            creatorReward: params.creatorReward,
            maker: params.maker,
            taker: params.taker,
            isFixedPrice: params.isFixedPrice,
            price: params.price.toString(),
            extra: params.extra.toString(),
            itemId: params.itemId.toString(),
            itemContract: params.itemContract,
        }

        return await this.contracts.nftMarketplace.methods
            .atomicMatch(order, params.buyerSignature, params.sellerSignature)
            .send({
                from: this.contracts.web3.eth.defaultAccount,
                gas: 2000000,
            })
    }

    approveTokens = async (from: string, amount: BN, to: string) => {
        return await this.contracts.CALToken.methods
            .approve(to, amount)
            .send({ from: from, gas: 120000 })
    }

    approveNFT = async (from: string, tokenId: BN, to: string) => {
        return await this.contracts.nftFactory.methods
            .approve(to, tokenId)
            .send({ from: from, gas: 120000 })
    }

    getBalanceOf = async (owner: string) => {
        return await this.contracts.CALToken.methods.balanceOf(owner).call()
    }

    getTokensApproval = async (from: string, to: string) => {
        return await this.contracts.CALToken.methods.allowance(from, to).call()
    }

    isNFTApproved = async (to: string, tokenId: BN) => {
        const approvedAddress = await this.contracts.nftFactory.methods
            .getApproved(tokenId)
            .call()
        return approvedAddress === to
    }
}
